import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@shopflo/ui/dialog";
import GenericButton from "components/common/buttons/GenericButton";
import { X } from "@phosphor-icons/react";
import { CheckoutModalType } from "lib/types/checkout";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";

interface ConfirmationPopupProps {
  popupTitle: string | JSX.Element;
  popupBody: string | JSX.Element;
  primaryBtnText?: string;
  onClickPrimaryBtn: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClickSecondaryBtn?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isOpen: boolean;
  closePopup: () => void;
  cancelBtnText?: string;
  btnsFlexDirection?: "flex-row" | "flex-col";
  modalType: CheckoutModalType;
  showSecondaryBtn?: boolean;
  id?: string;
}

const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({
  popupTitle,
  popupBody,
  primaryBtnText = "Confirm",
  onClickPrimaryBtn,
  isOpen = false,
  closePopup,
  cancelBtnText = "Cancel",
  onClickSecondaryBtn = closePopup,
  btnsFlexDirection = "flex-row",
  modalType,
  showSecondaryBtn = true,
  id,
}) => {
  const {
    state: { checkoutModal },
    actions: { setCheckoutModal },
  } = useCheckoutContext();

  const browserBackBtnHandler = (e: any) => {
    if (!Boolean(isOpen)) return;
    if (!Boolean(modalType)) return;
    if (modalType === "EXIT_CONFIRMATION") return;
    e.preventDefault();
    closePopup && closePopup();
  };

  // useBackButton(browserBackBtnHandler);

  useEffect(() => {
    if (!Boolean(isOpen)) return;
    if (!Boolean(modalType)) return;

    //Nested modals will have the modalType of their parent.
    if (checkoutModal === "NONE") {
      setCheckoutModal(modalType);
      return () => {
        setCheckoutModal("NONE");
      };
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={closePopup}>
      <DialogContent
        className=" w-screen h-screen p-0 bg-transparent border-none"
        id={id}
        container={document.getElementById("checkout-ui-root")}
      >
        {/* Not needed as we are using the full screen modal */}
        {/* <div className="fixed inset-0 z-40 bg-black/40" /> */}

        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="max-w-[23rem] max-h-80 bg-white rounded-lg shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b p-4">
              <DialogHeader className="flex w-full justify-between items-start">
                <div className="flex w-full justify-between">
                  {Boolean(popupTitle) &&
                    (typeof popupTitle === "string" ? (
                      <DialogTitle className="text-base font-medium text-carbon-dark">
                        {popupTitle}
                      </DialogTitle>
                    ) : (
                      <>{popupTitle}</>
                    ))}
                  <X
                    className="h-5 w-5 cursor-pointer text-coal-dark"
                    onClick={closePopup}
                  />
                </div>
                <div className="pt-3.5">
                  {Boolean(popupBody) &&
                    (typeof popupBody === "string" ? (
                      <DialogDescription className="text-sm font-normal text-coal-dark">
                        {popupBody}
                      </DialogDescription>
                    ) : (
                      <>{popupBody}</>
                    ))}
                </div>
              </DialogHeader>
            </div>

            <div
              className={`flex ${btnsFlexDirection} items-end justify-evenly ${btnsFlexDirection === "flex-col" ? `space-y-4` : ""
                } ${btnsFlexDirection === "flex-row" ? `space-x-4` : ""} p-4`}>
              <GenericButton
                id={checkoutModal === "COD_CONFIRMATION" ? "flo__payments__confirmCOD" : ""}
                height="h-14"
                width="w-full"
                customClass={`flex items-center justify-center text-base font-medium rounded-lg text-btnPrimaryText bg-primary-dark`}
                buttonText={primaryBtnText}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => onClickPrimaryBtn(event)}
              />

              {showSecondaryBtn && (
                <GenericButton
                  height="h-14"
                  width="w-full"
                  customClass="flex items-center justify-center text-base font-medium rounded-lg text-coal-dark border-2 border-gray-light"
                  buttonText={cancelBtnText}
                  onClick={onClickSecondaryBtn}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationPopup;
